import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

/*
 * i18n — Norwegian (Bokmål, default) + English.
 *
 * This is a typed, Paraglide-shaped layer: messages are functions, the active
 * locale is provider state, and `useT()` returns the message dictionary. The
 * locked stack is Paraglide-JS; we keep this hand-authored module deliberately
 * Paraglide-compatible (same `t.key()` call sites) so swapping in Paraglide's
 * compiled `m` is mechanical, without betting the build on its compile-time
 * module fetch. nb is the source-of-truth shape; `en` must structurally match.
 */

export type Locale = 'nb' | 'en';
export const LOCALES: readonly Locale[] = ['nb', 'en'] as const;
const STORAGE_KEY = 'nono.locale';

const nb = {
  appName: () => 'Nono',
  appTagline: () => 'Annonsekontroll',

  // navigation — group headings
  navAnalyse: () => 'ANALYSE',
  navAction: () => 'HANDLING',
  navBooking: () => 'Booking',
  navSettings: () => 'Innstillinger',
  // navigation — items
  navTruth: () => 'Sannheten',
  navOverview: () => 'Oversikt',
  navFunnel: () => 'Funnel',
  navTrends: () => 'Trender',
  navAds: () => 'Annonser',
  navLanding: () => 'Landingsside',
  navAlerts: () => 'Varsler',
  navPlan: () => 'Plan',
  navAttribution: () => 'Attribusjon',
  navQuiz: () => 'Quiz',
  navCalendar: () => 'Kalender',
  navLeads: () => 'Leads',
  navReferrals: () => 'Verving',
  navProfitability: () => 'Lønnsomhet',
  navBookings: () => 'Bookinger',
  navAvailability: () => 'Tilgjengelighet',
  navTargets: () => 'Mål',
  navRules: () => 'Regler',
  navMap: () => 'Nono-kartet',
  navContent: () => 'Innhold',
  navMeta: () => 'Meta',
  navMetaAccount: () => 'Kontoen',
  navMetaProduction: () => 'Produksjon',
  navMetaSystem: () => 'Systemet',
  navMetaDoors: () => 'Inngangsdører',
  navMetaHistory: () => 'Historikken',
  navMetaCustomers: () => 'Kunder',
  navCloseMenu: () => 'Lukk meny',

  // filter bar
  segAll: () => 'Alle',
  segLead: () => 'Lead',
  segBooking: () => 'Booking',
  resync: () => 'Synk',
  syncing: () => 'Synker …',
  synced: () => 'Synket',
  syncedAgo: (mins: number) => (mins < 1 ? 'Synket nå' : `Synket for ${mins} min siden`),
  methodology: () => 'Slik leses tallene',

  // auth / login
  signIn: () => 'Logg inn',
  signInWithGoogle: () => 'Logg inn med Google',
  signOut: () => 'Logg ut',
  loginHeadline: () => 'Annonsekontroll',
  loginSub: () => 'Intern beslutningsoversikt for Nono. Logg inn med din Nono-konto.',
  loginDenied: (email: string) => `${email} har ikke tilgang til dette panelet.`,
  notConfigured: () => 'Innlogging er ikke konfigurert. Kontakt administrator.',

  // overview / generic
  overviewTitle: () => 'Oversikt',
  overviewSub: () => 'Dagens beslutningssvar for betalt markedsføring.',
  loading: () => 'Laster …',
  errorTitle: () => 'Noe gikk galt',
  errorBody: () => 'Klarte ikke å hente data. Prøv igjen.',
  retry: () => 'Prøv igjen',
  signedInAs: () => 'Innlogget som',
  connectionOk: () => 'Tilkoblet API',

  // theme
  theme: () => 'Tema',
  themeDark: () => 'Mørk',
  themeLight: () => 'Lys',
  language: () => 'Språk',

  // cross-view commons
  total: () => 'Total',
  day: () => 'Dag',
  date: () => 'Dato',
  showAllColumns: () => 'Vis alle kolonner',
  showDecisionOnly: () => 'Kun beslutningstall',
  noData: () => 'Ingen data i perioden',
  noDataHint: () => 'Velg en annen periode, eller kjør en synk.',
  coloredVsTargets: () => 'farget mot mål',
  legendGood: () => 'på mål',
  legendWarn: () => 'innen 10 %',
  legendBad: () => 'over 10 % unna',
} as const;

type Dict = typeof nb;

const en: { [K in keyof Dict]: Dict[K] } = {
  appName: () => 'Nono',
  appTagline: () => 'Ad control',

  navAnalyse: () => 'ANALYSIS',
  navAction: () => 'ACTION',
  navBooking: () => 'Booking',
  navSettings: () => 'Settings',
  navTruth: () => 'The Truth',
  navOverview: () => 'Overview',
  navFunnel: () => 'Funnel',
  navTrends: () => 'Trends',
  navAds: () => 'Ads',
  navLanding: () => 'Landing page',
  navAlerts: () => 'Alerts',
  navPlan: () => 'Plan',
  navAttribution: () => 'Attribution',
  navQuiz: () => 'Quiz',
  navCalendar: () => 'Calendar',
  navLeads: () => 'Leads',
  navReferrals: () => 'Referrals',
  navProfitability: () => 'Profitability',
  navBookings: () => 'Bookings',
  navAvailability: () => 'Availability',
  navTargets: () => 'Targets',
  navRules: () => 'Rules',
  navMap: () => 'Nono map',
  navContent: () => 'Content',
  navMeta: () => 'Meta',
  navMetaAccount: () => 'Account',
  navMetaProduction: () => 'Production',
  navMetaSystem: () => 'System',
  navMetaDoors: () => 'Entry points',
  navMetaHistory: () => 'History',
  navMetaCustomers: () => 'Customers',
  navCloseMenu: () => 'Close menu',

  segAll: () => 'All',
  segLead: () => 'Lead',
  segBooking: () => 'Booking',
  resync: () => 'Sync',
  syncing: () => 'Syncing …',
  synced: () => 'Synced',
  syncedAgo: (mins: number) => (mins < 1 ? 'Synced just now' : `Synced ${mins} min ago`),
  methodology: () => 'How to read this',

  signIn: () => 'Sign in',
  signInWithGoogle: () => 'Sign in with Google',
  signOut: () => 'Sign out',
  loginHeadline: () => 'Ad control',
  loginSub: () => "Nono's internal decision cockpit. Sign in with your Nono account.",
  loginDenied: (email: string) => `${email} does not have access to this dashboard.`,
  notConfigured: () => 'Sign-in is not configured. Contact your administrator.',

  overviewTitle: () => 'Overview',
  overviewSub: () => "Today's decision answers for paid marketing.",
  loading: () => 'Loading …',
  errorTitle: () => 'Something went wrong',
  errorBody: () => 'Could not load data. Please try again.',
  retry: () => 'Retry',
  signedInAs: () => 'Signed in as',
  connectionOk: () => 'API connected',

  theme: () => 'Theme',
  themeDark: () => 'Dark',
  themeLight: () => 'Light',
  language: () => 'Language',

  total: () => 'Total',
  day: () => 'Day',
  date: () => 'Date',
  showAllColumns: () => 'Show all columns',
  showDecisionOnly: () => 'Decision metrics only',
  noData: () => 'No data in this period',
  noDataHint: () => 'Pick another range, or run a sync.',
  coloredVsTargets: () => 'colored vs targets',
  legendGood: () => 'on target',
  legendWarn: () => 'within 10%',
  legendBad: () => 'more than 10% off',
};

const dictionaries: Record<Locale, Dict> = { nb, en };

function readInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'nb' || stored === 'en') return stored;
  } catch {
    /* SSR / private mode */
  }
  return 'nb';
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Dict;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale: setLocaleState, t: dictionaries[locale] }),
    [locale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within <LocaleProvider>');
  return ctx;
}

/** Returns the active message dictionary. Call sites: `const t = useT(); t.navOverview()`. */
export function useT(): Dict {
  return useLocaleContext().t;
}

/** Returns { locale, setLocale } for the language switcher. */
export function useLocale(): { locale: Locale; setLocale: (l: Locale) => void } {
  const { locale, setLocale } = useLocaleContext();
  return { locale, setLocale };
}

/**
 * Resolve a view-local message bundle against the active locale. Lets a feature
 * co-locate its own strings (`const m = useViewMessages(MESSAGES)`) without
 * editing the central dictionary — keeps view-specific copy nb+en and isolated.
 */
export function useViewMessages<T>(messages: Record<Locale, T>): T {
  return messages[useLocaleContext().locale];
}
